/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import {
  lobAbbreviationToText,
  recordToRow,
  strToCloseLoopStatus,
  subtractMonths,
  trimZipcodeToFiveDigits,
  unformatedDateToFormatted,
  type TMemberHistoryRecord,
  type TMemberHistoryTableRow,
  type TMemberInfo,
  type TNotFulfilledReasonSchema,
  type TUnmetReasonTextSchema,
} from '@repo/utils';
import { API } from '@src/api';
import { type ApiResponse, type UpdateReferralRequest, type UpdateScreenerRequest } from '@src/api.types';
import { memberInContextAtom, vendorAtom } from '@src/global-state';
import { sessionService } from '@src/services/_session_service';
import {
  getDeclinedScreener,
  getFeatureFlagsFromStorage,
  saveCheckBenefitScreening,
  saveFromSearch,
} from '@src/utills/storage';
import { Breadcumbs } from '../BreadCumbs/Breadcrumbs';
import { EditMemberHistory } from '../EditMemberHistory/EditMemberHistory';
import { EditMemberHistoryFeedbackModal } from '../EditMemberHistory/EditMemberHistoryFeedbackModal';
import { MemberHistoryTable } from '../MemberHistoryTable/MemberHistoryTable';
import { MemberProfileLinksPanel } from '../MemberProfileLinksPanel/MemberProfileLinksPanel';
import { MemberProfileMemberInfoPanel } from '../MemberProfileMemberInfoPanel/MemberProfileMemberInfoPanel';
import { MspLisQuizContainer } from '../MspLisQuiz/MspLisQuizContainer';
import { Home } from '@src/routes/Home';

// Constants
const HISTORY_MONTHS = 18;
const UNFULFILLED_DAYS_THRESHOLD = 45;

const LOADING_OVERLAY_STYLES = {
  zIndex: 999,
  backgroundColor: '#FFFFFF',
  opacity: 0.75,
  position: 'fixed' as const,
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  margin: 0,
  top: 0,
  left: 0,
};

// Helper Functions
const formatDateOfBirth = (dob: string | undefined): string | undefined => {
  if (!dob) return undefined;

  try {
    if (dob.includes('/')) {
      const [month, day, year] = dob.split('/');
      return `${month}/${day}/${year}`;
    }
    if (dob.includes('-')) {
      const [year, month, day] = dob.split('-');
      return `${month}/${day}/${year}`;
    }
  } catch (error) {
    console.error('Error formatting date of birth:', error);
  }

  return undefined;
};

export const MemberProfile = () => {
  // Hooks
  const { navigate } = useRouter();
  const [memberInContext, setMemberInContext] = useAtom(memberInContextAtom);
  const [, setVendorCache] = useAtom(vendorAtom);

  // Loading States
  const [isLoading, setIsLoading] = useState(false);
  const [checkedReferralHistory, setCheckedReferralHistory] = useState(false);
  const [checkedMemberScreening, setCheckedMemberScreening] = useState(false);

  // Feature States
  const [disableScreening, setDisableScreening] = useState(false);
  const [showMspLisLink, setShowMspLisLink] = useState(false);

  // Modal States
  const [showSkipAssessmentModal, setShowSkipAssessmentModal] = useState(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [warningMsg, setWarningMsg] = useState('');

  // SDOH History States
  const [rawSdohHistory, setRawSdohHistory] = useState<TMemberHistoryRecord[]>([]);
  const [sdohTableRows, setSdohTableRows] = useState<TMemberHistoryTableRow[] | undefined>(undefined);
  const [updatingSdohRecord, setUpdatingSdohRecord] = useState<TMemberHistoryTableRow | undefined>(undefined);

  // Edit Response State
  const [editHistoryResponse, setEditHistoryResponse] = useState<
    ApiResponse<undefined> | null | undefined
  >(undefined);

  // Quiz State
  const [takingMspLisQuiz, setTakingMspLisQuiz] = useState(false);

  // Refs
  const eighteenMonthsAgo = useMemo(() => subtractMonths(new Date(), HISTORY_MONTHS), []);
  const mostRecentScreeningDate = useRef<Date>(eighteenMonthsAgo);

  // Feature Flags
  const featureFlags = useMemo(() => getFeatureFlagsFromStorage(), []);



  // Computed Values
  const formattedDOB = useMemo(() => formatDateOfBirth(memberInContext?.DOB), [memberInContext?.DOB]);
  const isDataLoading = isLoading || !checkedReferralHistory || !checkedMemberScreening;

  // API Functions
  const fetchMemberReferralHistory = useCallback(async (formattedInfo: TMemberInfo) => {
    try {
      const resp = await API.getMemberReferralHistory({
        firstName: formattedInfo.first_name,
        lastName: formattedInfo.last_name,
        memberId: formattedInfo.member_id,
        dob: formattedInfo.DOB,
        monthsOfHistory: HISTORY_MONTHS,
      });

      if (!('data' in resp) || !resp.data || !('success' in resp.data) || !resp.data.success) {
        throw new Error('Referral history request was not successful');
      }

      const history = resp.data.sdohDetails;
      setRawSdohHistory([...history]);

      const rows: TMemberHistoryTableRow[] = history
        .filter((record) =>
          record.identificationReferralFulfillment === 'REF' ||
          record.identificationReferralFulfillment === 'IDN'
        )
        .map((record) => recordToRow(record, [...history], true))
        .filter((row): row is TMemberHistoryTableRow => row !== undefined)
        .sort((a, b) => {
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          return dateB - dateA;
        });

      setSdohTableRows(rows);

      if (resp.data.memberDetails?.lastScreeningDate) {
        const referralHistoryScreenDate = unformatedDateToFormatted(
          resp.data.memberDetails.lastScreeningDate
        );
        if (
          referralHistoryScreenDate &&
          referralHistoryScreenDate > mostRecentScreeningDate.current
        ) {
          mostRecentScreeningDate.current = referralHistoryScreenDate;
        }
      }
    } catch (error) {
      console.error('Error fetching member referral history:', error);
    } finally {
      setCheckedReferralHistory(true);
    }
  }, []);

  const checkDisableScreening = useCallback(async (formattedInfo: TMemberInfo) => {
    // Early return for medSup
    if (formattedInfo?.medSup) {
      setDisableScreening(true);
      return;
    }

    // Check provider when UniteUs API is not enabled
    const provider = await API.fetchProviderToUse({
      lineOfBusiness: formattedInfo?.lineOfBusiness,
      zipcode: formattedInfo?.zipCode,
    });
    if (provider?.status === 'success') {
      setVendorCache({ zipcode: formattedInfo?.zipCode ?? '', response: provider });
    }
    if (provider?.provider?.keyword === 'UniteUs') {
      setDisableScreening(true);
      setIsWarningModalOpen(false);
      return;
    }
    if(provider?.status !== 'success'){
      setDisableScreening(false);
      setIsWarningModalOpen(true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setWarningMsg((provider as any)?.message);
    }

    // Check benefit screening
    const resp = await API.checkDisableScreening({
      lineOfBusiness: formattedInfo?.lineOfBusiness,
      consumerId: formattedInfo?.ConsumerId,
      zipcode: formattedInfo?.zipCode,
    });

    if (resp.status === 'success') {
      saveCheckBenefitScreening(resp.data);
    }

    if (resp.data === true) {
      setDisableScreening(true);
      return;
    }
    setDisableScreening(false);
  }, []);

  const getMemberScreeningHistory = useCallback(async (memberId: string) => {
    try {
      const resp = await API.getMemberScreeningHistory(memberId);

      if (!resp) {
        throw new Error('No response from member screening history API');
      }

      if (resp?.data?.createdDate) {
        const memberCreatedDate = new Date(resp.data.createdDate.substring(0, 19));
        if (memberCreatedDate && memberCreatedDate > mostRecentScreeningDate.current) {
          mostRecentScreeningDate.current = memberCreatedDate;
        }
      }
    } catch (error) {
      console.error('Error fetching member screening history:', error);
    } finally {
      setCheckedMemberScreening(true);
    }
  }, []);

  // Event Handlers
  const handleSubmitHistoryUpdate = useCallback(async (
    closedLoopStatus: string,
    reasonNotFulfilled: TUnmetReasonTextSchema | TNotFulfilledReasonSchema
  ) => {
    if (!updatingSdohRecord) return;

    const updatingDate = updatingSdohRecord.date;
    if (!updatingDate) {
      console.error('No date found for updating record');
      return;
    }

    const updating = rawSdohHistory.find((record) => {
      if (record.match !== updatingSdohRecord.match) return false;

      const formattedDate = unformatedDateToFormatted(record.serviceDate);
      if (!formattedDate) return false;

      const recordDate = new Date(formattedDate);
      const updateDate = new Date(updatingDate);
      return recordDate.getTime() === updateDate.getTime();
    });

    if (!updating) {
      console.error('Could not find original record for updated record', updatingSdohRecord);
      return;
    }

    const memberInfo: UpdateReferralRequest['memberDetails'] = {
      nameFirst: memberInContext?.first_name ?? '',
      nameLast: memberInContext?.last_name ?? '',
      id: memberInContext?.member_id ?? '',
      dateOfBirth: formattedDOB ?? '',
      lineOfBusiness: memberInContext?.lineOfBusiness ?? '',
      zipcode: memberInContext?.addrZipCode ?? '',
      gender: memberInContext?.gender ?? 'U',
      enterpriseIndividualIdentifier: memberInContext?.enterpriseIndividualIdentifier ?? '',
      consumerId: memberInContext?.ConsumerId ?? '',
    };

    let resp: ApiResponse<undefined> | null | undefined = undefined;

    try {
      switch (updating.identificationReferralFulfillment) {
        case 'REF':
          resp = await API.submitSdohReferralUpdate(
            updating as UpdateReferralRequest['record'],
            memberInfo,
            closedLoopStatus as UpdateReferralRequest['updateType'],
            reasonNotFulfilled as TNotFulfilledReasonSchema
          );
          break;
        case 'IDN':
          resp = await API.submitSdohScreenerUpdate(
            updating as UpdateScreenerRequest['record'],
            memberInfo,
            closedLoopStatus as UpdateScreenerRequest['updateType'],
            reasonNotFulfilled as TUnmetReasonTextSchema
          );
          break;
      }

      if (resp === null) {
        console.error('Error submitting SDOH record update');
        setUpdatingSdohRecord(undefined);
        setEditHistoryResponse({ status: 'error' } as ApiResponse<undefined>);
        return;
      }

      if (resp?.status === 'success' && resp?.data === undefined) {
        const newRecord: TMemberHistoryTableRow = {
          ...updatingSdohRecord,
          closedLoopStatus: strToCloseLoopStatus(closedLoopStatus),
          reasonNotFulfilled,
          isEditable: false,
        };

        const updatedRows = sdohTableRows?.map((row) =>
          row.match === updatingSdohRecord.match ? newRecord : row
        );

        setSdohTableRows(updatedRows);
        setUpdatingSdohRecord(undefined);
        setEditHistoryResponse(resp);
      }
    } catch (error) {
      console.error('Error in handleSubmitHistoryUpdate:', error);
      setUpdatingSdohRecord(undefined);
      setEditHistoryResponse({ status: 'error' } as ApiResponse<undefined>);
    }
  }, [updatingSdohRecord, rawSdohHistory, memberInContext, formattedDOB, sdohTableRows]);

  const handleCboSearchClick = useCallback(() => {
    const declined = getDeclinedScreener();
    const alreadyDeclined = declined.includes(
      memberInContext?.member_id ?? 'No member ID'
    );

    if (disableScreening || alreadyDeclined) {
      setShowSkipAssessmentModal(false);
      navigate(`/search?zipcode=${trimZipcodeToFiveDigits(memberInContext?.zipCode ?? '')}`);
      return;
    }

    setShowSkipAssessmentModal(true);
  }, [disableScreening, memberInContext, navigate]);

  const handleNavigateToScreener = useCallback(() => {
    navigate('/question');
  }, [navigate]);

  const handleNavigateToPlanBenefits = useCallback(() => {
    saveFromSearch(false);
    navigate('/planbenefits');
  }, [navigate]);

  const handleMspLisLinkClick = useCallback(() => {
    setTakingMspLisQuiz(true);
  }, []);

  // Effects
  useEffect(() => {
    const shouldHideMspLisLink = (
      (memberInContext?.ifp && featureFlags?.agent_ifp?.enabled) ||
      memberInContext?.lineOfBusiness === 'E&I' ||
      memberInContext?.lineOfBusiness === 'C&S' ||
      !featureFlags?.agent_msp_lis_visibility?.enabled
    );

    setShowMspLisLink(!shouldHideMspLisLink);
  }, [memberInContext, featureFlags]);

  useEffect(() => {
    const fetchSessionInfo = async () => {
      setIsLoading(true);
      try {
        const sessionInfo = await API.getSessionInfo();
        const formattedInfo = sessionService.formatSessionData(sessionInfo) as TMemberInfo;
        setMemberInContext(formattedInfo);

        await Promise.all([
          fetchMemberReferralHistory(formattedInfo),
          checkDisableScreening(formattedInfo),
          getMemberScreeningHistory(formattedInfo?.member_id),
        ]);
      } catch (error) {
        console.error('Error fetching session info:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSessionInfo();
  }, [fetchMemberReferralHistory, checkDisableScreening, getMemberScreeningHistory, setMemberInContext]);

  // Render
  return (
    <React.Fragment>
      {/* Loading Overlay */}
      {isDataLoading && (
        <div style={LOADING_OVERLAY_STYLES}>
          <LoadingSpinner
            size="xl"
            isLoading={isDataLoading}
            ariaLoadingLabel="Loading member profile"
          />
        </div>
      )}

      {/* Skip Assessment Modal */}
      {showSkipAssessmentModal && (
        <Home
          isOpen={showSkipAssessmentModal}
          onClose={() => setShowSkipAssessmentModal(false)}
        />
      )}

      {/* Member Info Section */}
      <div style={{ backgroundColor: '#edf8fa' }}>
        <Grid
          style={{
            padding: '5.5%',
            width: '100%',
            marginTop: '0px',
            paddingTop: '5rem',
          }}
        >
          <Grid.Col span={{ sm: 12, md: 12, lg: 6 }} style={{ paddingTop: '0%' }}>
            <div>
              <Breadcumbs screen="MEMBER_PROFILE" />
              <MemberProfileMemberInfoPanel
                memberFirstName={memberInContext?.first_name ?? ''}
                memberLastName={memberInContext?.last_name ?? ''}
                memberId={memberInContext?.member_id ?? ''}
                memberDateOfBirth={formattedDOB ?? ''}
                memberZipCode={memberInContext?.addrZipCode ?? ''}
                memberLineOfBusiness={
                  lobAbbreviationToText(memberInContext?.lineOfBusiness) ?? ''
                }
              />
            </div>
          </Grid.Col>

          <Grid.Col span={{ sm: 12, md: 12, lg: 6 }} style={{ paddingTop: '0px' }}>
            <MemberProfileLinksPanel
              memberInContext={memberInContext!}
              lastSdohNeedsQuizDate={dayjs(mostRecentScreeningDate.current).format('MMM D, YYYY')}
              showSdohNeedsQuizLink={!disableScreening}
              showMspLisQuizLink={showMspLisLink}
              onClickCboSearchLink={handleCboSearchClick}
              onClickSdohNeedsQuizLink={handleNavigateToScreener}
              onClickMspLisQuizLink={handleMspLisLinkClick}
              onClickPlanBenefitsLink={handleNavigateToPlanBenefits}
            />
          </Grid.Col>
        </Grid>
      </div>

      {/* MSP/LIS Quiz or Member History Section */}
      {takingMspLisQuiz ? (
        <MspLisQuizContainer setShowMspLisQuiz={setTakingMspLisQuiz} />
      ) : (
        <div
          style={{
            paddingTop: '5%',
            paddingBottom: '15px',
            paddingLeft: '5%',
            paddingRight: '5%',
          }}
        >
          <div>
            <Heading size="md">Member History</Heading>
            <div>
              <Text fontSize="md">
                Referrals and Screeners that are not fulfilled after {UNFULFILLED_DAYS_THRESHOLD} days are{' '}
                <strong>bolded</strong> in the grid below.
                <br />
                If you make any changes to the &apos;closed loop status&apos;, you will be
                able to see your update immediately. However, please note that
                if you leave the page or exit Community Connector and return
                later, your edit may not be immediately visible. The changes
                will be saved but may take up to 24 hours to be reflected within
                the Member History grid.
              </Text>
            </div>
          </div>

          {sdohTableRows !== undefined && (
            <React.Fragment>
              <Layout.Group
                alignLayout="between"
                style={{ paddingBottom: '0.5em' }}
              >
                <Text fontSize="md" fontWeight="bold">Last {HISTORY_MONTHS} months of data</Text>
                <Text fontSize="md">
                  {`Showing ${sdohTableRows.length} of ${sdohTableRows.length} records`}
                </Text>
              </Layout.Group>

              <MemberHistoryTable
                rows={sdohTableRows}
                onClickEdit={({ row }) => setUpdatingSdohRecord(row.original)}
                checkEditDisabled={(row) => !row.original.isEditable}
              />

              {updatingSdohRecord && (
                <EditMemberHistory
                  updating={updatingSdohRecord}
                  isOpen={true}
                  setIsOpen={() => setUpdatingSdohRecord(undefined)}
                  onCloseDrawer={() => setUpdatingSdohRecord(undefined)}
                  onSubmitUpdate={handleSubmitHistoryUpdate}
                />
              )}
            </React.Fragment>
          )}
        </div>
      )}

      {/* Warning Modal */}
      <ModalDialog
        header="Warning"
        isOpen={isWarningModalOpen}
        size="$md"
        onClose={() => setIsWarningModalOpen(false)}
      >
        <div data-cy="warning-modal-member-profile">
          {warningMsg}
        </div>
      </ModalDialog>

      {/* Edit History Feedback Modal */}
      <EditMemberHistoryFeedbackModal
        editHistoryResponse={editHistoryResponse}
        setEditHistoryResponse={setEditHistoryResponse}
      />
    </React.Fragment>
  );
};
